import React, { useMemo } from 'react'

import * as ReactDialog from '@radix-ui/react-dialog'
import { cn } from 'src/helpers/style'

import {
  AppIcon,
  AppIconNames,
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'

import { DialogAction, DialogControllingProps, DistributiveOmit } from './types'

export type DefaultDialogProps = {
  title?: string | React.ReactElement
  content?: string | React.ReactElement
  actions?: DialogAction[]
  isLoading?: boolean
  dismissable?: boolean
  showCloseButton?: boolean
  onClose?: () => void
  children?: React.ReactNode
  /**
   * Override classes for the dialog content container. Provided classes are
   * merged with (and can replace) the defaults via twMerge.
   */
  contentClassName?: string
  /**
   * Override classes for the backdrop overlay.
   */
  overlayClassName?: string
} & DialogControllingProps

export type DefaultDialogServiceOptions = DistributiveOmit<
  DefaultDialogProps,
  'triggerElement' | 'isOpen'
>

const baseOverlayClassName = cn(
  'fixed inset-0 z-[11] bg-gray-700/50',
  'animate-dialog-overlay',
)

const baseContentClassName = cn(
  'fixed left-1/2 top-1/2 z-[12] -translate-x-1/2 -translate-y-1/2',
  'flex flex-col justify-between bg-white rounded-default p-8 shadow-md text-secondary',
  'md:min-w-[680px] transition focus:outline-none',
  'animate-dialog-content',
)

export const DefaultDialog: React.FC<DefaultDialogProps> = ({
  title,
  actions,
  content,
  isLoading,
  dismissable = true,
  showCloseButton = true,
  children,
  contentClassName,
  overlayClassName,
  ...rest
}) => {
  const ActionWrapper = useMemo(
    () =>
      'triggerElement' in rest
        ? ({ children: actionChildren }: { children: React.ReactNode }) => (
            <ReactDialog.Close asChild>{actionChildren}</ReactDialog.Close>
          )
        : React.Fragment,
    [rest],
  )

  const isCustomLayout = children !== undefined

  const handleOpenChange = (open: boolean): void => {
    if (open) return
    if ('onClose' in rest) rest.onClose?.()
  }

  return (
    <ReactDialog.Root
      open={'isOpen' in rest ? rest.isOpen : undefined}
      onOpenChange={'isOpen' in rest ? handleOpenChange : undefined}
    >
      {'triggerElement' in rest && (
        <ReactDialog.Trigger asChild={typeof rest.triggerElement !== 'string'}>
          {rest.triggerElement}
        </ReactDialog.Trigger>
      )}
      <ReactDialog.Portal>
        <ReactDialog.Overlay
          className={cn(baseOverlayClassName, overlayClassName)}
        />
        <ReactDialog.Content
          onEscapeKeyDown={event => {
            if (!dismissable) {
              event.preventDefault()
            }
          }}
          onInteractOutside={e => {
            if (!dismissable) {
              e.preventDefault()
            }
          }}
          className={cn(
            isCustomLayout
              ? cn(
                  'fixed left-1/2 top-1/2 z-[12] -translate-x-1/2 -translate-y-1/2',
                  'focus:outline-none animate-dialog-content',
                )
              : baseContentClassName,
            contentClassName,
          )}
        >
          {isCustomLayout ? (
            children
          ) : (
            <>
              <ReactDialog.Title className="flex justify-between items-center pb-4 gap-4">
                <Typography
                  asChild={typeof title !== 'string'}
                  weight={TypographyWeight.semibold}
                  className="text-primary text-xl"
                >
                  {title}
                </Typography>
                {showCloseButton && (
                  <ReactDialog.Close
                    className="rounded-default size-8 hover:bg-gray-100 transition"
                    onClick={'onClose' in rest ? rest.onClose : undefined}
                  >
                    <AppIcon name={AppIconNames.close} />
                  </ReactDialog.Close>
                )}
              </ReactDialog.Title>

              {content && (
                <ReactDialog.Description
                  asChild={typeof content === 'string'}
                  className="mb-8"
                >
                  {typeof content === 'string' ? (
                    <Typography variant={TypographyVariant.p}>
                      {content}
                    </Typography>
                  ) : (
                    content
                  )}
                </ReactDialog.Description>
              )}

              <div className="flex justify-end gap-7">
                {actions?.map((action, index) => {
                  if (typeof action.content === 'string') {
                    const lastIndex = index === actions.length - 1
                    let variant = action.variant
                    if (!variant) {
                      variant = lastIndex
                        ? ButtonVariant.primary
                        : ButtonVariant.ghost
                    }
                    return (
                      <ActionWrapper key={index}>
                        <Button
                          label={action.content}
                          variant={variant}
                          onClick={action.onClick}
                          loading={lastIndex && isLoading}
                          disabled={isLoading || action.disabled}
                        />
                      </ActionWrapper>
                    )
                  }
                  return <ActionWrapper>{action.content}</ActionWrapper>
                })}
              </div>
            </>
          )}
        </ReactDialog.Content>
      </ReactDialog.Portal>
    </ReactDialog.Root>
  )
}
